import React, { Component } from 'react';
import { inject } from 'mobx-react';

import { AGENT_STATUSES, AGENT_TYPES, AGENT_CAPABILITIES, AGENT_GROUPS } from '@kentik/ui-shared/synthetics/constants';
import { Box, Button, Checkbox, Flex, Link, Text, HTMLTable } from 'core/components';
import { safelyParseJSON } from 'core/util';
import { BaseAgentRenderer } from 'app/views/synthetics/components/AgentRenderer';

// Note: do not use @observer here because it won't hear about normal state variable changes and it's not dealing with observables anyway
@inject('$auth')
class AgentSelectorFormBody extends Component {
  static defaultProps = {
    emptyText: 'No agents found'
  };

  state = {
    isSectionOpen: {}
  };

  constructor(props) {
    super(props);

    if (window.localStorage) {
      this.state.isSectionOpen = safelyParseJSON(localStorage.getItem('isAgentSectionExpanded')) || {};
    }
  }

  filterOptions(options, agentGroup, overrideSection = null) {
    const seenAgentValues = new Set();
    return agentGroup || overrideSection
      ? options.filter((agent) => {
          // Check if agent.value has already been seen. If so, filter out this agent.
          if (seenAgentValues.has(agent.value)) {
            return false;
          }
          // Add agent.value to the Set to mark it as seen.
          seenAgentValues.add(agent.value);

          // Pending App Agents
          if (agentGroup === AGENT_GROUPS.PENDING_APP) {
            return (
              agent.agent_status === AGENT_STATUSES.WAIT &&
              agent.agent_type === AGENT_TYPES.GLOBAL &&
              agent?.capabilities?.includes(AGENT_CAPABILITIES.WEB)
            );
          }

          // All Pending Agents
          if (agentGroup === AGENT_GROUPS.PENDING && agent.agent_type === AGENT_TYPES.GLOBAL) {
            return agent.agent_status === AGENT_STATUSES.WAIT;
          }

          // Private Network Agent
          if (agentGroup === AGENT_GROUPS.PRIVATE_NETWORK) {
            return agent.agent_type === AGENT_TYPES.PRIVATE && !agent?.capabilities?.includes(AGENT_CAPABILITIES.WEB);
          }

          // Private App Agent
          if (agentGroup === AGENT_GROUPS.PRIVATE_APP) {
            return agent.agent_type === AGENT_TYPES.PRIVATE && agent?.capabilities?.includes(AGENT_CAPABILITIES.WEB);
          }

          // Global Network Agent (RUST & NETWOK)
          if (agentGroup === AGENT_GROUPS.GLOBAL_NETWORK) {
            return agent.agent_type === AGENT_TYPES.GLOBAL && !agent?.capabilities?.includes(AGENT_CAPABILITIES.WEB);
          }
          // Global App Agent (NODE)
          if (agentGroup === AGENT_GROUPS.GLOBAL_APP) {
            return agent.agent_type === AGENT_TYPES.GLOBAL && agent?.capabilities?.includes(AGENT_CAPABILITIES.WEB);
          }

          // This is right now being used for showing pending agents as well as workers in a cluster, for cluster management
          if (overrideSection) {
            // the option filter is defined in the override section object as a filter to determine which agents to show in which section.
            // This is allowing us more flexibilty in the agent selector modal.
            return overrideSection.optionFilter(agent);
          }

          return false;
        })
      : options;
  }

  allChecked(agentGroup) {
    const { agents, options } = this.props;
    const optionsFiltered = this.filterOptions(options, agentGroup);
    return !!optionsFiltered.length && optionsFiltered.every((option) => agents.includes(option.value));
  }

  handleCheckAll = (checked, agentGroup) => {
    const { agents, onChange, options } = this.props;
    const optionsFiltered = this.filterOptions(options, agentGroup);
    const optionValues = optionsFiltered.map((option) => option.value);

    let next;
    if (checked) {
      // add options to next that are not already in agents
      next =
        agents.length === 0
          ? optionValues
          : agents.concat(
              optionValues.reduce((acc, option) => {
                if (!agents.includes(option)) {
                  acc.push(option);
                }
                return acc;
              }, [])
            );
    } else {
      // remove options from next if they are already in agents
      next = agents.reduce((acc, agent) => {
        if (!optionValues.includes(agent)) {
          acc.push(agent);
        }
        return acc;
      }, []);
    }
    onChange(next);
  };

  handleItemChange = (option, selected) => {
    const { agents, multi, onChange } = this.props;

    if (!multi) {
      onChange(option.value);
    } else {
      const agentIdx = agents.findIndex((agent) => option.value === agent);

      // IMPORTANT! AgentSelectorDisplay is decorated as an @observer. To ensure a re-render in that component
      // we must create a new agents array via concat() rather than mutating the array via push() or splice()
      if (selected) {
        // If the item is now selected, we send onChange with it included if it wasn't already in there
        if (agentIdx === -1) {
          onChange(agents.concat(option.value));
        }
        // If the item is no longer selected, we send onChange with it removed if it was already in there
      } else if (agentIdx !== -1) {
        agents.splice(agentIdx, 1);
        onChange([].concat(agents));
      }
    }
  };

  handleExpandSection = (label) => {
    const { isSectionOpen } = this.state;

    delete isSectionOpen[label];

    this.setSectionOpenState(isSectionOpen);
  };

  handleCollapseSection = (label) => {
    const { isSectionOpen } = this.state;

    isSectionOpen[label] = false;

    this.setSectionOpenState(isSectionOpen);
  };

  setSectionOpenState(isSectionOpen) {
    this.setState({ isSectionOpen });
    if (window.localStorage) {
      localStorage.setItem('isAgentSectionExpanded', JSON.stringify(isSectionOpen));
    }
  }

  renderOptionRow(option) {
    const { agents, small, large, multi, showIdColumn } = this.props;

    return (
      <tr key={option.value}>
        <Box as="td">
          <Checkbox
            className={multi ? undefined : 'bp4-radio'}
            checked={multi ? agents.includes(option.value) : parseInt(agents) === option.value}
            onChange={(checked) => this.handleItemChange(option, checked)}
            small={small}
            large={large}
            mb={0}
          />
        </Box>
        <Box as="td">
          <BaseAgentRenderer {...option} wrapAgentLabel />
        </Box>
        <Box as="td">{option.locationInfo}</Box>
        <Box as="td">{option.agent_family === 'DUAL' ? 'v4 + v6' : option.agent_family}</Box>
        {showIdColumn && <Box as="td">{option.value}</Box>}
      </tr>
    );
  }

  renderSection(agentGroup, emptyText, overrideSection = null) {
    const {
      emptyText: emptyTextProp,
      hideGlobalAgents,
      showIdColumn,
      options: _options,
      allOptions: _allOptions,
      multi
    } = this.props;

    const { isSectionOpen } = this.state;
    let label;
    if (agentGroup === AGENT_GROUPS.PRIVATE_APP) {
      label = 'Private App Agent';
    } else if (agentGroup === AGENT_GROUPS.PRIVATE_NETWORK) {
      label = 'Private Network Agent';
    } else if (agentGroup === AGENT_GROUPS.GLOBAL_APP) {
      label = 'Global App Agent';
    } else if (
      agentGroup === AGENT_GROUPS.PENDING_APP ||
      agentGroup === AGENT_GROUPS.PENDING_NETWORK ||
      agentGroup === AGENT_GROUPS.PENDING
    ) {
      label = 'Pending Agent';
    } else if (overrideSection) {
      label = overrideSection.label;
    } else {
      label = 'Global Network Agent';
    }

    const options = this.filterOptions(_options, agentGroup, overrideSection);
    const allOptions = this.filterOptions(_allOptions, agentGroup);
    const colspan = showIdColumn ? 5 : 4;

    const isOpen = isSectionOpen[label] !== false || hideGlobalAgents;

    const onClick = () => (isOpen ? this.handleCollapseSection(label) : this.handleExpandSection(label));

    // If the options were filtered or we don't have a custom emptyText passed in here, use the emptyText prop
    const useEmptyTextProp = allOptions.length > options.length || !emptyText;
    return (
      <React.Fragment key={agentGroup || overrideSection.label}>
        {/* TODO: deprecate browserAgents #7660 ... show this only if !hideGlobalAgents */}
        <Box as="tr" bg="subnavBackground">
          <td colSpan={colspan}>
            <Flex alignItems="center">
              {multi && !overrideSection?.hideSectionHeaderCheckbox && this.renderCheckBox(agentGroup)}
              <Button
                onClick={onClick}
                icon={isOpen ? 'caret-down' : 'caret-right'}
                disabled={options.length === 0}
                mr="2px"
                minimal
                small
              />
              <Text fontWeight="bold">{label}</Text>
            </Flex>
          </td>
        </Box>
        {options.length === 0 && (
          <tr>
            <Box as="td" colSpan={colspan} pl="12px">
              {useEmptyTextProp && (
                <Text as="div" muted>
                  {emptyTextProp}
                </Text>
              )}
              {!useEmptyTextProp && emptyText}
            </Box>
          </tr>
        )}
        {isOpen && options.map((option) => this.renderOptionRow(option))}
      </React.Fragment>
    );
  }

  renderCheckBox = (agentGroup) => {
    const { small, large } = this.props;
    return (
      <Checkbox
        checked={this.allChecked(agentGroup)}
        onChange={(checked) => this.handleCheckAll(checked, agentGroup)}
        small={small}
        large={large}
        mb={0}
      />
    );
  };

  render() {
    const {
      $auth,
      agents,
      emptyText,
      hideGlobalAgents,
      hidePrivateAgents,
      hideNetworkAgents,
      showPendingAgents,
      showIdColumn,
      onChange,
      multi,
      overrideSections,
      ...boxProps
    } = this.props;

    // Flipping all of these to make for easier logic below
    const showGlobalAgents = !hideGlobalAgents;
    const showPrivateAgents = !hidePrivateAgents;
    const showNetworkAgents = !hideNetworkAgents;

    const noPrivateAgentsText = (
      <>
        You do not have any private agents configured. Visit <Link to="/v4/synthetics/agents">Agents</Link> to get
        started with private agents.
      </>
    );
    const noPrivateNodeAgentsText = (
      <>
        You do not have any private App agents installed. Visit <Link to="/v4/synthetics/agents">Agents</Link>
        to find instructions to deploy Private App Agents.
      </>
    );

    return (
      <Box bg="appBackground" {...boxProps} role="list">
        <HTMLTable striped>
          <colgroup>
            <col style={{ width: 34 }} />
            <col style={{ width: 'calc(40% - 36px)' }} />
            <col style={{ width: '40%' }} />
            <col style={{ width: showIdColumn ? '13%' : '20%' }} />
            {showIdColumn && <col style={{ width: '7%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th>{multi && this.renderCheckBox()}</th>
              <th>Agent</th>
              <th>Location</th>
              <th>IP Version</th>
              {showIdColumn && <th>ID</th>}
            </tr>
          </thead>
          <tbody>
            {/* Pending Agent */}
            {showPendingAgents &&
              this.renderSection(
                hideNetworkAgents ? AGENT_GROUPS.PENDING_APP : AGENT_GROUPS.PENDING,
                'No Pending agents'
              )}

            {/* Private Network Agent */}
            {showPrivateAgents &&
              showNetworkAgents &&
              this.renderSection(AGENT_GROUPS.PRIVATE_NETWORK, noPrivateAgentsText)}

            {/* Private App Agent */}
            {showPrivateAgents && this.renderSection(AGENT_GROUPS.PRIVATE_APP, noPrivateNodeAgentsText)}

            {/* Global Network Agent */}
            {showGlobalAgents && showNetworkAgents && this.renderSection(AGENT_GROUPS.GLOBAL_NETWORK)}

            {/* Global App Agent */}
            {showGlobalAgents && this.renderSection(AGENT_GROUPS.GLOBAL_APP)}

            {/* This override section allows us to specify which sections and which agents to show. */}
            {overrideSections && overrideSections.map((section) => this.renderSection(null, null, section))}
          </tbody>
        </HTMLTable>
      </Box>
    );
  }
}

export default AgentSelectorFormBody;
